/*
 * Fixed giftbox greeting-card picker (PHASE 4A) - PDP skin only.
 *
 * This element does NOT touch the cart. It renders as a nicer face over
 * Dawn's native <variant-selects> for a Shopify Fixed Bundle: choosing a
 * card tile simply checks the matching native option radio and lets Dawn's
 * own product-info flow update price / media / [name="id"] / submit button.
 * Add-to-cart stays 100% native -> one native bundle line in cart/checkout.
 *
 * Mapping: tile.dataset.optionValue === native radio .value (option value string).
 * No variant IDs, SKUs or handles are hardcoded.
 *
 * NOTE: Dawn replaces the whole <variant-selects> element on every variant
 * change (product-info.updateOptionValues -> viewTransition). So the native
 * picker MUST be looked up live on every use, never cached.
 */
if (!customElements.get('nm-giftbox-card-picker')) {
  customElements.define(
    'nm-giftbox-card-picker',
    class NmGiftboxCardPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.sectionId || '';
        this.productInfo = this.closest('product-info');

        // Progressive enhancement: only hide the native picker once this
        // element is wired up. The class lives on <product-info> (which is
        // not replaced on variant change), so CSS keeps every future
        // <variant-selects> hidden too. If the JS never runs, the native
        // picker stays visible and the product remains fully usable.
        if (this.nativeSelects() && this.productInfo) {
          this.productInfo.classList.add('nm-giftbox-cardpicker-active');
        }

        this.onChange = this.handleChange.bind(this);
        this.addEventListener('change', this.onChange);

        // Align the native picker with the tile checked on load
        // (in case ?variant= or defaults differ from the "none" tile).
        const checked = this.querySelector('input[type="radio"][data-option-value]:checked');
        if (checked) this.syncNative(checked.dataset.optionValue);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.initialized = false;
      }

      nativeSelects() {
        return (
          (this.sectionId && document.getElementById('variant-selects-' + this.sectionId)) ||
          this.productInfo?.querySelector('variant-selects') ||
          document.querySelector('variant-selects') ||
          null
        );
      }

      handleChange(event) {
        const input = event.target;
        if (!input.matches || !input.matches('input[type="radio"][data-option-value]')) return;
        this.syncNative(input.dataset.optionValue);
      }

      syncNative(optionValue) {
        if (optionValue == null) return;
        const vs = this.nativeSelects();
        if (!vs) return;

        // Radio (button/swatch) picker
        const radios = vs.querySelectorAll('input[type="radio"]');
        for (const radio of radios) {
          if (radio.value !== optionValue) continue;
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        // Dropdown picker fallback
        const select = vs.querySelector('select');
        if (select) {
          const match = Array.from(select.options).find((o) => o.value === optionValue);
          if (match && select.value !== optionValue) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }
  );
}
